import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { usePersonaTrendApi } from '@/common/api/modules/persona'
import { PersonaTransition, PersonaTrendSeries } from '@/common/api/params/persona'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import StackedAreaChart, {
  STACKED_AREA_COLORS,
  STACKED_AREA_OTHERS_COLOR
} from '@/components/view/atoms/chart/StackedAreaChart'
import Heading from '@/components/view/atoms/heading/Heading'
import PersonaAvatar, { PersonaAvatarSize } from '@/components/view/atoms/image/PersonaAvatar'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import InsightPanel, { InsightProgress } from '@/components/container/modules/persona/InsightPanel'
import { usePersonaInsight } from '@/components/container/modules/persona/useInsight'

/** グラフに出す系列の上限。超えた分は「その他」に畳む（色を巡回させない） */
const MAX_SERIES = 8

const RANGES = [
  { days: 30, label: '30日' },
  { days: 90, label: '90日' },
  { days: 180, label: '180日' }
]

/** 増減。0 は「変わらない」で、色を付けない */
const Delta = ({ value }: { value: number }) => {
  if (value === 0) {
    return (
      <Text size={12} color={TextColor.TERTIARY} nowrap>
        ±0
      </Text>
    )
  }
  return (
    <Text size={12} color={value > 0 ? TextColor.SUCCESS : TextColor.CRITICAL} nowrap>
      {value > 0 ? `+${value.toLocaleString()}` : value.toLocaleString()}
    </Text>
  )
}

/** 数値タイル。`Text` はインライン要素なので、行ごとに div で包まないと横に潰れる */
const Metric = ({ label, value, muted }: { label: string; value: string; muted?: boolean }) => (
  <div className='min-w-[160px]'>
    <Text size={12} color={TextColor.SECONDARY}>
      {label}
    </Text>
    <div>
      {muted ? (
        <Text size={14} color={TextColor.SECONDARY}>
          {value}
        </Text>
      ) : (
        <Text size={24} weight='bold'>
          {value}
        </Text>
      )}
    </div>
  </div>
)

/** 日付ラベル。年は要らないので M/D にする */
const shortDate = (iso: string) => {
  const [, m, d] = iso.split('-')
  return `${Number(m)}/${Number(d)}`
}

/** 遷移の1ブロック。移動・新規・離脱は意味が違うので同じ表に混ぜない */
const FlowTable = ({
  title,
  empty,
  rows,
  hideFrom = false,
  hideTo = false
}: {
  title: string
  empty: string
  rows: PersonaTransition[]
  hideFrom?: boolean
  hideTo?: boolean
}) => (
  <div>
    <Text size={13} weight='bold'>
      {title}（{rows.reduce((n, t) => n + t.customers, 0).toLocaleString()} 人）
    </Text>
    {rows.length === 0 ? (
      <div className='mt-1'>
        <Text size={12} color={TextColor.SECONDARY}>
          {empty}
        </Text>
      </div>
    ) : (
      <div className='mt-2'>
        <Table>
          <thead>
            <TableRow head>
              {!hideFrom && (
                <TableHeadItem>
                  <Text size={12}>変更前</Text>
                </TableHeadItem>
              )}
              {!hideTo && (
                <TableHeadItem>
                  <Text size={12}>変更後</Text>
                </TableHeadItem>
              )}
              <TableHeadItem>
                <Text size={12}>顧客数</Text>
              </TableHeadItem>
            </TableRow>
          </thead>
          <tbody>
            {rows.map((t) => (
              <TableRow key={`${t.from_persona ?? 'new'}=>${t.to_persona ?? 'out'}`}>
                {!hideFrom && (
                  <TableRowItem>
                    <Text size={13}>{t.from_name}</Text>
                  </TableRowItem>
                )}
                {!hideTo && (
                  <TableRowItem>
                    <Text size={13}>{t.to_name}</Text>
                  </TableRowItem>
                )}
                <TableRowItem>
                  <Text size={14} weight='bold' nowrap>
                    {t.customers.toLocaleString()} 人
                  </Text>
                </TableRowItem>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </div>
    )}
  </div>
)

const PersonaTrendPage = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)
  const [days, setDays] = useState(30)

  const { data, isLoading } = usePersonaTrendApi({ site_id: siteId, days })

  // 示唆は画面で選んでいる期間に追従する。グラフが30日で示唆が90日だと読み手が混乱する
  const insight = usePersonaInsight(siteId, days)

  // 上位8体だけ色を割り当て、残りは「その他」に畳む。
  // 9体目に色を作ると、色覚特性で見分けられない組み合わせが混ざる
  const chart = useMemo(() => {
    if (!data || data.series.length === 0) return null
    const top = data.series.slice(0, MAX_SERIES)
    const rest = data.series.slice(MAX_SERIES)
    const items = top.map((s, i) => ({
      title: s.name,
      values: s.values,
      color: STACKED_AREA_COLORS[i]
    }))
    if (rest.length > 0) {
      items.push({
        title: `その他 ${rest.length} 体`,
        values: data.dates.map((_, i) => rest.reduce((sum, s) => sum + (s.values[i] ?? 0), 0)),
        color: STACKED_AREA_OTHERS_COLOR
      })
    }
    return { labels: data.dates.map(shortDate), items }
  }, [data])

  const colorOf = (index: number) =>
    index < MAX_SERIES ? STACKED_AREA_COLORS[index] : STACKED_AREA_OTHERS_COLOR

  // 「新規割当」「割当が外れた」を混ぜると、本来見たいペルソナ間の移動が埋もれる。
  // 意味が違うものを1つの表に並べない
  const flows = useMemo(() => {
    const all = data?.transitions ?? []
    const between = all.filter((t) => t.from_persona && t.to_persona)
    const added = all.filter((t) => !t.from_persona && t.to_persona)
    const removed = all.filter((t) => t.from_persona && !t.to_persona)
    const sum = (list: typeof all) => list.reduce((n, t) => n + t.customers, 0)
    return {
      between,
      added,
      removed,
      betweenCount: sum(between),
      addedCount: sum(added),
      removedCount: sum(removed)
    }
  }, [data])

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='ペルソナ推移'
        description='誰が何人で、どこからどこへ動いたかを見ます。記録のある期間だけを描き、無い期間は埋めません。成果（売上・CVR）はここでは扱いません。'
      />

      <Content>
        {isLoading || !data ? (
          <Area>
            <AreaContent>
              <Text color={TextColor.SECONDARY}>読み込み中...</Text>
            </AreaContent>
          </Area>
        ) : (
          <>
            <Area>
              <AreaContent>
                <div className='mb-3 flex flex-wrap items-center gap-2'>
                  {RANGES.map((r) => (
                    <Button
                      key={r.days}
                      color={days === r.days ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
                      onClick={() => setDays(r.days)}
                    >
                      {r.label}
                    </Button>
                  ))}
                  <span className='flex-1' />
                  <Text size={12} color={TextColor.SECONDARY}>
                    {data.dates.length > 0
                      ? `${data.dates[0]} 〜 ${data.dates[data.dates.length - 1]}（記録のある ${data.dates.length} 日）`
                      : '記録なし'}
                  </Text>
                </div>

                <div className='flex flex-wrap gap-x-10 gap-y-4'>
                  <Metric
                    label='いま主ペルソナが決まっている顧客'
                    value={`${data.total_current.toLocaleString()} 人`}
                  />
                  <Metric label='出ているペルソナ' value={`${data.series.length} 体`} />
                  {data.has_history ? (
                    <>
                      <Metric
                        label='期間内に別のペルソナへ移った'
                        value={`${flows.betweenCount.toLocaleString()} 人`}
                      />
                      <Metric
                        label='新たに割り当たった'
                        value={`${flows.addedCount.toLocaleString()} 人`}
                      />
                      <Metric
                        label='割当が外れた'
                        value={`${flows.removedCount.toLocaleString()} 人`}
                      />
                    </>
                  ) : (
                    <Metric label='期間内に動いた顧客' value='記録なし' muted />
                  )}

                  {/* 件数の横に置く。読んだ流れのまま「で、どういうこと？」を押せる位置 */}
                  <div className='ml-auto flex flex-col items-end justify-center gap-1'>
                    <Button
                      color={ButtonColor.PRIMARY}
                      loading={insight.isMutating}
                      onClick={insight.run}
                    >
                      AIペルソナ状況示唆
                    </Button>
                    <InsightProgress state={insight} />
                  </div>
                </div>
              </AreaContent>
            </Area>

            <InsightPanel state={insight} />

            <Area>
              <Heading subText='ペルソナごとに、その日「主」だった顧客数を積み上げています。色は識別のためだけのもので、順位や良し悪しは表しません。'>
                構成の推移
              </Heading>
              <AreaContent>
                {!data.has_history ? (
                  <div className='mt-2'>
                    <Text size={13} color={TextColor.SECONDARY}>
                      {data.note ?? 'まだ推移の記録がありません'}
                    </Text>
                  </div>
                ) : chart ? (
                  <div className='mt-3 h-80'>
                    <StackedAreaChart labels={chart.labels} items={chart.items} unit='人' />
                  </div>
                ) : (
                  <div className='mt-2'>
                    <Text size={13} color={TextColor.SECONDARY}>
                      まだ主ペルソナが決まった顧客がいません
                    </Text>
                  </div>
                )}
              </AreaContent>
            </Area>

            <Area>
              <Heading subText='期間の初日と最終日の差です。判定は毎日 07:10 JST に走ります。'>
                ペルソナごとの増減
              </Heading>
              <AreaContent>
                {data.series.length === 0 ? (
                  <div className='mt-2'>
                    <Text size={13} color={TextColor.SECONDARY}>
                      表示できるペルソナがありません
                    </Text>
                  </div>
                ) : (
                  <div className='mt-3'>
                    <Table>
                      <thead>
                        <TableRow head>
                          <TableHeadItem>
                            <Text size={12}>ペルソナ</Text>
                          </TableHeadItem>
                          <TableHeadItem>
                            <Text size={12}>状態</Text>
                          </TableHeadItem>
                          <TableHeadItem>
                            <Text size={12}>初日</Text>
                          </TableHeadItem>
                          <TableHeadItem>
                            <Text size={12}>最終日</Text>
                          </TableHeadItem>
                          <TableHeadItem>
                            <Text size={12}>増減</Text>
                          </TableHeadItem>
                          <TableHeadItem>
                            <Text size={12}>構成比</Text>
                          </TableHeadItem>
                        </TableRow>
                      </thead>
                      <tbody>
                        {data.series.map((s: PersonaTrendSeries, i: number) => (
                          <TableRow key={s.persona_id}>
                            <TableRowItem>
                              <div className='flex items-center gap-2'>
                                {/* 色見本。グラフの帯と同じ色を並べ、色だけに頼らず名前も出す */}
                                <span
                                  className='h-3 w-3 shrink-0 rounded-sm'
                                  style={{ background: colorOf(i) }}
                                />
                                <PersonaAvatar
                                  emoji={s.avatar_emoji}
                                  color={s.avatar_color}
                                  size={PersonaAvatarSize.SMALL}
                                />
                                <div className='min-w-0'>
                                  <Text size={13}>{s.name}</Text>
                                  <div>
                                    <Text size={10} color={TextColor.TERTIARY}>
                                      {s.persona_id}
                                    </Text>
                                  </div>
                                </div>
                              </div>
                            </TableRowItem>
                            <TableRowItem>
                              <Text
                                size={12}
                                color={s.active ? TextColor.SUCCESS : TextColor.SECONDARY}
                                nowrap
                              >
                                {s.active ? '有効' : '採用のみ'}
                              </Text>
                            </TableRowItem>
                            <TableRowItem>
                              <Text size={12} nowrap>
                                {s.first.toLocaleString()}
                              </Text>
                            </TableRowItem>
                            <TableRowItem>
                              <Text size={14} weight='bold' nowrap>
                                {s.last.toLocaleString()}
                              </Text>
                            </TableRowItem>
                            <TableRowItem>
                              <Delta value={s.delta} />
                            </TableRowItem>
                            <TableRowItem>
                              <Text size={12} nowrap>
                                {data.total_current > 0
                                  ? `${Math.round((s.last / data.total_current) * 1000) / 10}%`
                                  : '—'}
                              </Text>
                            </TableRowItem>
                          </TableRow>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </AreaContent>
            </Area>

            <Area>
              <Heading subText='主ペルソナが変わった顧客の流れです。「新規割当」はそれまで誰でもなかった顧客、「割当が外れた」はどの条件にも当たらなくなった顧客です。'>
                顧客の遷移
              </Heading>
              <AreaContent>
                {!data.has_history ? (
                  <div className='mt-2'>
                    <Text size={13} color={TextColor.SECONDARY}>
                      {data.note ?? 'まだ推移の記録がありません'}
                    </Text>
                  </div>
                ) : !data.has_transitions ? (
                  <div className='mt-2'>
                    <Text size={13} color={TextColor.SECONDARY}>
                      この期間に主ペルソナが変わった顧客はいません
                    </Text>
                  </div>
                ) : (
                  <div className='mt-3 flex flex-col gap-5'>
                    <FlowTable
                      title='別のペルソナへ移った'
                      empty='ペルソナ間の移動はありません'
                      rows={flows.between}
                    />
                    <FlowTable
                      title='新たに割り当たった'
                      empty='新たに割り当たった顧客はいません'
                      rows={flows.added}
                      hideFrom
                    />
                    <FlowTable
                      title='割当が外れた'
                      empty='割当が外れた顧客はいません'
                      rows={flows.removed}
                      hideTo
                    />
                  </div>
                )}
              </AreaContent>
            </Area>
          </>
        )}
      </Content>
    </Main>
  )
}

export default PersonaTrendPage
